// Canonical "current semester" anchor for portal surfaces.
//
// Extracted from homeworkTiers.js (#414) so the homework section and the
// progress section's attendance rate agree about which semester is current.
// They render on the same page for the same parent, so a fork here shows up
// as two surfaces disagreeing about what "this term" means.
//
// The anchor is the greatest (start_date, semester_id) tuple present — the
// SAME ordering the server and the billing surface use (portal_billing.py
// sorts ascending on (start_date or "", id or "")). It is NEVER derived from
// session or due dates: a rescheduled session must not redefine the term.
//
// Unknown provenance counts as CURRENT. A missing semester_id (deploy skew,
// or a deleted class row) must never push live work into a collapsed
// previous-semester tier, and must never silently drop a session out of the
// attendance denominator.

export function semesterTuple(item) {
  return [item?.semesterStartDate || '', item?.semesterId || '']
}

export function tupleGreater(a, b) {
  return a[0] > b[0] || (a[0] === b[0] && a[1] > b[1])
}

// Greatest (start_date, id) tuple across items, or null when no item carries
// a semester id at all. `readTuple` lets callers keep their own field
// spelling (the wire uses snake_case, the normalized client shape uses
// camelCase) without forking the ordering rule itself.
export function currentSemesterAnchor(items, readTuple = semesterTuple) {
  let anchor = null
  for (const item of items || []) {
    const tuple = readTuple(item)
    // No semester id => no provenance to rank. Such items are CURRENT (see
    // isCurrentSemester); they must not get a vote on where the anchor sits.
    if (!tuple[1]) continue
    if (anchor === null || tupleGreater(tuple, anchor)) anchor = tuple
  }
  return anchor
}

// True when `item` belongs to the current semester. Absent anchor (nothing
// carries provenance) and absent semester id both read as current.
export function isCurrentSemester(item, anchor, readTuple = semesterTuple) {
  if (anchor === null) return true
  const tuple = readTuple(item)
  if (!tuple[1]) return true
  return tuple[1] === anchor[1]
}
